import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  TextField,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { REQUEST } from '../../../../utils/const/formTypes';
import useStyles from './styles';

const TaskDetailComponent = ({ taskDetail }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isRequest = taskDetail.type === REQUEST;

  const columnLabelAndKey = [
    { label: 'ID', key: 'id' },
    { label: t('taskDetailComponent.tableHeaders.companyName'), key: 'companyName' },
    { label: t('taskDetailComponent.tableHeaders.projectName'), key: 'projectName' },
    { label: t('taskDetailComponent.tableHeaders.title'), key: 'title' },
    { label: t('taskDetailComponent.tableHeaders.type'), key: 'type' },
    { label: t('taskDetailComponent.tableHeaders.label'), key: 'label' },
    { label: t('taskDetailComponent.tableHeaders.requiredGeoreference'), key: 'requiredGeoreference' },
    ...(taskDetail.requiredGeoreference
      ? [{ label: t('taskDetailComponent.tableHeaders.maxDistance'), key: 'maxDistance' }]
      : []),
    { label: t('taskDetailComponent.tableHeaders.revisionPolicy'), key: 'revisionPolicy' },
    ...(isRequest
      ? [
        { label: t('taskDetailComponent.tableHeaders.needReview'), key: 'needReview' },
        { label: t('taskDetailComponent.tableHeaders.executionSurvey'), key: 'executionSurvey' },
        { label: t('taskDetailComponent.tableHeaders.reviewSurvey'), key: 'reviewSurvey' },
      ]
      : []),
    { label: t('taskDetailComponent.tableHeaders.aiAnalysisRequired'), key: 'aiAnalysisRequired' },
    { label: t('taskDetailComponent.tableHeaders.verticalForm'), key: 'verticalForm' },
    { label: t('taskDetailComponent.tableHeaders.teleaudit'), key: 'teleaudit' },
    { label: t('taskDetailComponent.tableHeaders.switchStock'), key: 'switchStock' },
    { label: t('taskDetailComponent.tableHeaders.description'), key: 'description' },
  ];

  const confirmationLabels = {
    applicationReview: t('task.taskEdition.needConfirmation.applicantReview'),
    otherReview: t('task.taskEdition.needConfirmation.otherReview'),
    no: t('task.taskEdition.needConfirmation.no'),
  };

  const parseValue = (value, key) => {
    if (value === null || value === undefined || value === '') return '-';
    if (key === 'needReview') return confirmationLabels[value] || value;
    if (typeof value === 'boolean') {
      return value ? t('common.yes') : t('common.no');
    }
    return value;
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h1" className={classes.title} gutterBottom>
          {t('taskDetailComponent.title')}
        </Typography>
        <Box ml={2}>
          <IconButton
            aria-label={t('taskDetailComponent.tableHeaders.edit')}
            component={Link}
            to={`/tasks/${taskDetail.id}/edit-detail`}
          >
            <EditIcon titleAccess={t('taskDetailComponent.tableHeaders.edit')} />
          </IconButton>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableBody>
            {columnLabelAndKey.map((item) => (
              <TableRow key={item.key}>
                <TableCell component="th" scope="row">
                  <Typography className={classes.semiBold}>{item.label}</Typography>
                </TableCell>
                <TableCell align="right">
                  {item.key === 'description'
                    ? (
                      <TextField
                        value={parseValue(taskDetail[item.key], item.key)}
                        disabled
                        multiline minRows={4}
                        variant="outlined"
                        className={classes.textField}
                      />
                    )
                    : parseValue(taskDetail[item.key], item.key)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

TaskDetailComponent.propTypes = {
  taskDetail: PropTypes.shape({
    id: PropTypes.number,
    companyName: PropTypes.string,
    projectName: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    requiredGeoreference: PropTypes.bool,
    maxDistance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    revisionPolicy: PropTypes.bool,
    needReview: PropTypes.string,
    executionSurvey: PropTypes.string,
    reviewSurvey: PropTypes.string,
    aiAnalysisRequired: PropTypes.bool,
    verticalForm: PropTypes.bool,
    teleaudit: PropTypes.bool,
    switchStock: PropTypes.bool,
    description: PropTypes.string,
  }),
};

TaskDetailComponent.defaultProps = {
  taskDetail: {
    id: null,
    companyName: null,
    projectName: null,
    title: null,
    type: null,
    label: null,
    requiredGeoreference: null,
    maxDistance: null,
    revisionPolicy: null,
    needReview: null,
    executionSurvey: null,
    reviewSurvey: null,
    aiAnalysisRequired: null,
    verticalForm: null,
    teleaudit: null,
    switchStock: null,
    description: null,
  },
};

export default TaskDetailComponent;
